import { resolveReturnToPath } from "@thalamos/common";

import { createRoute } from "./createRoute.js";

/**
 * Where the user was heading before being sent to sign in. Captured so an
 * emailed link survives the round trip through Auth0 rather than dumping them
 * on the home page.
 *
 * Consecutive slashes are collapsed: a browser will happily navigate to
 * `https://host//path`, and the doubled slash then reads as protocol-relative
 * once appended to an origin, which is also what would send a redirect loop
 * back through here.
 */
export const captureReturnTo = (location: {
  pathname: string;
  search: string;
}): string => {
  const pathname = location.pathname.replace(/\/+/g, "/");
  return `${pathname}${location.search}`;
};

/**
 * Where to go after signing in or choosing a context. Falls back to the home
 * page for anything unusable — including a destination pointing back at the
 * page doing the redirecting, which would otherwise loop.
 */
export const consumeReturnTo = (search: string): string => {
  // Validated before the slashes are collapsed, not after: collapsing first
  // would turn `//evil.example.com` into the rooted `/evil.example.com`, which
  // is same-origin and so harmless, but is not a destination anyone asked for.
  // Better to reject it and start at the top.
  const path = resolveReturnToPath(new URLSearchParams(search).get("returnTo"));

  if (path === "") {
    return createRoute.home();
  }

  const normalised = path.replace(/\/+/g, "/");

  if (normalised.startsWith(createRoute.sessionContextSelector())) {
    return createRoute.home();
  }

  return normalised;
};
